import React, { ReactNode, useCallback } from "react";
import { connect } from "react-redux";

import { faClose } from "@fortawesome/pro-solid-svg-icons/faClose";
import { faExclamationCircle } from "@fortawesome/pro-solid-svg-icons/faExclamationCircle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cn from "classnames";
import { defineMessages, useIntl } from "react-intl";

import useRerenderer from "@mapmycustomers/shared/util/hook/useRerenderer";
import { Button } from "@mapmycustomers/ui";

import localSettings from "@app/config/LocalSettings";
import { getUsageLimits } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";

import styles from "./RecordLimitAlert.module.scss";

const messages = defineMessages({
  action: {
    id: "navbar.alert.recordLimit.action",
    defaultMessage: "View Plans & Pricing",
    description: "Action button title in record limit alert",
  },
  titleApproaching: {
    id: "navbar.alert.recordLimit.title.approaching",
    defaultMessage:
      "Approaching Your Record Limit: <span>You've used {used, number, ##.} of {limit, number, ##.} records ({percent, number, ::percent}). Consider upgrading soon.</span>",
  },
  titleOutOfLimit: {
    id: "navbar.alert.recordLimit.title.outOfLimit",
    defaultMessage:
      "Record Limit Reached: <span>You've reached {limit, number, ##.} records. Upgrade to continue adding customers.</span>",
  },
});

const RecordLimitAlert = ({ usageLimits }: ReturnType<typeof mapStateToProps>) => {
  const intl = useIntl();

  const rerender = useRerenderer();

  const handleCloseClick = useCallback(() => {
    localSettings.setRecordLimitAlertHidden(true);
    rerender();
  }, [rerender]);

  if (localSettings.getRecordLimitAlertHidden() || usageLimits.records.limit <= 0) {
    return null;
  }

  const limit90 = usageLimits.records.limit * 0.9;
  if ((usageLimits.records.usage ?? 0) < limit90) {
    return null;
  }

  const outOfLimit = (usageLimits.records.usage ?? 0) >= usageLimits.records.limit;

  return (
    <section className={cn(styles.container, { [styles.outOfLimit]: outOfLimit })}>
      <div className={styles.header}>
        <FontAwesomeIcon className={styles.icon} icon={faExclamationCircle} />

        {intl.formatMessage(outOfLimit ? messages.titleOutOfLimit : messages.titleApproaching, {
          limit: usageLimits.records.limit,
          percent: usageLimits.records.limit
            ? (usageLimits.records.usage ?? 0) / usageLimits.records.limit
            : 1,
          span: (text: ReactNode) => <span className={styles.subtitle}>{text}</span>,
          used: usageLimits.records.usage ?? 0,
        })}
      </div>
      <div className={styles.actions}>
        <Button href="https://mapmycustomers.com/pricing/" target="_blank" type="primary">
          {intl.formatMessage(messages.action)}
        </Button>
        <Button
          icon={<FontAwesomeIcon icon={faClose} />}
          iconOnly
          onClick={handleCloseClick}
          type="control"
        />
      </div>
    </section>
  );
};

const mapStateToProps = (state: RootState) => ({
  usageLimits: getUsageLimits(state)!,
});

export default connect(mapStateToProps)(RecordLimitAlert);
